import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    TextField,
    Box,
    Alert,
    Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { nl as nlLocale } from 'date-fns/locale';

interface CandidateConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (availableAt: string) => void;
    workerName: string;
    isLoading?: boolean;
}

/**
 * Dialog shown when promoting a worker to CANDIDATE status.
 * Requires the user to set an "Available from" date before confirming.
 * This triggers the sync to the saleslist system.
 */
const CandidateConfirmationDialog: React.FC<CandidateConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    workerName,
    isLoading = false,
}) => {
    const [availableAt, setAvailableAt] = useState<Date | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            // Default to today's date
            setAvailableAt(new Date());
            setError(null);
        }
    }, [open]);

    const handleConfirm = () => {
        if (!availableAt) {
            setError('Selecteer een datum waarop de kandidaat beschikbaar is');
            return;
        }

        // Format date as ISO string (YYYY-MM-DD)
        const dateStr = availableAt.toISOString().split('T')[0];
        onConfirm(dateStr);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Kandidaat promoveren</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <DialogContentText>
                        Je staat op het punt om <strong>{workerName}</strong> te promoveren naar de status{' '}
                        <strong>CANDIDATE</strong>.
                    </DialogContentText>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        Bij deze statuswijziging wordt automatisch een verkoopkans aangemaakt in de verkooplijst.
                        Vul hieronder de beschikbaarheidsdatum in.
                    </Typography>
                </Alert>

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nlLocale}>
                    <DatePicker
                        label="Beschikbaar vanaf *"
                        value={availableAt}
                        onChange={(newValue) => {
                            setAvailableAt(newValue);
                            setError(null);
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: !!error,
                                helperText: error || 'De datum waarop de kandidaat beschikbaar is voor plaatsing',
                            },
                        }}
                        minDate={new Date()}
                    />
                </LocalizationProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={isLoading}>
                    Annuleren
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={isLoading || !availableAt}
                >
                    {isLoading ? 'Bezig...' : 'Bevestigen'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CandidateConfirmationDialog;
